import { apiRequest } from "../utils/api";

let cachedCampaignRows = null;
// The in-flight fetch promise, if a fetch is currently running. Distinct from
// cachedCampaignRows: without this, getKnownCampaignCodes and
// getKnownClientCompanyNames called concurrently (both see a null cache
// before either one's request resolves) each independently paginate through
// every page, doubling the request volume instead of sharing one fetch.
let inFlightFetch = null;

const CAMPAIGN_MAPPING_PAGE_SIZE = 200; // endpoint's own max limit (campaignMappingModel.js)

// Walks every page of /api/campaign-mappings and returns the rows fetched so
// far, plus whether every page was actually walked. A later page failing
// (e.g. a transient network blip) stops the walk but still returns whatever
// earlier pages already succeeded - a partial list is still strictly better
// for PII detection than throwing every row away, since each retained row is
// a real value the matcher can still catch. `complete` lets the caller tell
// "every page succeeded" apart from "we stopped early after an error" - the
// two must not be cached the same way (see fetchCampaignRows below).
const fetchAllCampaignRowPages = async () => {
  const rows = [];
  let page = 1;
  let totalPages = 1;
  let complete = false;

  try {
    do {
      const response = await apiRequest.get("/api/campaign-mappings", {
        params: {
          page,
          limit: CAMPAIGN_MAPPING_PAGE_SIZE,
          include_campaign_name: true,
          include_sub_functional_unit_name: true,
        },
        showLoader: false,
        skipNotification: true,
      });
      const body = response?.data || {};
      rows.push(...(body.data || []));
      totalPages = body.totalPages || 1;
      page += 1;
    } while (page <= totalPages);
    complete = true;
  } catch (err) {
    console.warn("Failed to fetch campaign mappings:", err.message);
  }

  return { rows, complete };
};

// Shared by getKnownCampaignCodes/getKnownClientCompanyNames so both known-value
// PII matchers derive from a single fetch of the same campaign_mapping rows,
// rather than each hitting the endpoint independently.
//
// Uses /api/campaign-mappings rather than /api/getSupportAdminAllCampaigns -
// the latter never selects/decrypts campaign_name at all, so
// getKnownClientCompanyNames() below was always returning an empty list
// (COACH-2094). This endpoint is paginated, so all pages are walked to avoid
// silently missing campaigns past the first one - a truncated list here is a
// PII-detection false negative, not just a display gap.
const fetchCampaignRows = async () => {
  if (cachedCampaignRows) return cachedCampaignRows;
  if (inFlightFetch) return inFlightFetch;

  // The shared promise itself must resolve to a plain rows array - both a
  // caller that starts the fetch and a concurrent caller that reuses this
  // same in-flight promise (see the `if (inFlightFetch)` branch above) get
  // whatever this promise resolves to, so the caching decision has to happen
  // inside the chain rather than after a bare `await`.
  inFlightFetch = fetchAllCampaignRowPages()
    .then(({ rows, complete }) => {
      // Only cache a fetch that walked every page. Caching a partial result
      // (stopped early by e.g. a transient network blip on page 3 of 10)
      // would permanently pin an incomplete list for the rest of the
      // session, even though the very next call might succeed in full - and
      // for PII detection, a permanently truncated list is a silent false
      // negative, not just a display gap. An incomplete fetch instead gets a
      // chance to retry (from scratch) on the next call, same as a total
      // failure (0 rows) already did before this change.
      if (complete) cachedCampaignRows = rows;
      return rows;
    })
    .finally(() => {
      inFlightFetch = null;
    });

  return inFlightFetch;
};

/**
 * Fetches the known campaign codes (functional_unit/sub_functional_unit,
 * e.g. "OPS-MYS") used to flag campaign identifiers in PII detection.
 */
const getKnownCampaignCodes = async () => {
  const rows = await fetchCampaignRows();
  const codes = rows.flatMap(row => [row.functional_unit, row.sub_functional_unit]).filter(Boolean);
  return Array.from(new Set(codes));
};

/**
 * Fetches the known client company names (decrypted campaign_name) used to
 * flag a client's company name in PII detection. A campaign_name that fails
 * to decrypt comes back null/empty and is filtered out here rather than
 * surfacing a decryption error.
 */
const getKnownClientCompanyNames = async () => {
  const rows = await fetchCampaignRows();
  const names = rows.map(row => row.campaign_name).filter(Boolean);
  return Array.from(new Set(names));
};

/**
 * Fetches the known sub-functional-unit display names (decrypted
 * sub_functional_unit_name) used to flag a sub-functional unit's name in PII
 * detection - distinct from the plaintext functional_unit/sub_functional_unit
 * *codes* already covered by getKnownCampaignCodes above (COACH-2113). A
 * sub_functional_unit_name that fails to decrypt comes back null/empty and is
 * filtered out here, same as getKnownClientCompanyNames above.
 */
const getKnownSubFunctionalUnitNames = async () => {
  const rows = await fetchCampaignRows();
  const names = rows.map(row => row.sub_functional_unit_name).filter(Boolean);
  return Array.from(new Set(names));
};

export default {
  getKnownCampaignCodes,
  getKnownClientCompanyNames,
  getKnownSubFunctionalUnitNames,
};
